/**
 * Bug Report Utilities
 * Captures browser context, device info, console errors,
 * failed network requests, and recent user actions for bug reports.
 */

// ── Console error ring buffer (max 10) ──────────────────────────────────────
const recentErrors: Array<{ message: string; source?: string; timestamp: string }> = [];
const MAX_ERRORS = 10;

// ── Failed network request ring buffer (max 15) ────────────────────────────
const recentFailedRequests: Array<{
  url: string;
  method: string;
  status: number;
  timestamp: string;
}> = [];
const MAX_FAILED_REQUESTS = 15;

// ── User action ring buffer (max 20) ────────────────────────────────────────
const recentUserActions: Array<{
  type: "click" | "navigation";
  target?: string;
  to?: string;
  timestamp: string;
}> = [];
const MAX_USER_ACTIONS = 20;

function pushAction(action: (typeof recentUserActions)[number]) {
  recentUserActions.push(action);
  if (recentUserActions.length > MAX_USER_ACTIONS) recentUserActions.shift();
}

/** Initialize global error, network, and action listeners — call once in main.tsx */
export function initErrorCapture() {
  // ── JS error listeners (existing) ─────────────────────────────────────────
  window.addEventListener("error", (event) => {
    recentErrors.push({
      message: event.message || String(event.error),
      source: event.filename
        ? `${event.filename}:${event.lineno}:${event.colno}`
        : undefined,
      timestamp: new Date().toISOString(),
    });
    if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
  });

  window.addEventListener("unhandledrejection", (event) => {
    recentErrors.push({
      message:
        event.reason instanceof Error
          ? event.reason.message
          : String(event.reason),
      source: "unhandledrejection",
      timestamp: new Date().toISOString(),
    });
    if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
  });

  // ── Failed network request tracking ───────────────────────────────────────
  const originalFetch = window.fetch;
  window.fetch = async function patchedFetch(...args: Parameters<typeof fetch>) {
    let res: Response;
    try {
      res = await originalFetch.apply(this, args);
    } catch (err) {
      // Network error (offline, CORS, etc.)
      const reqUrl = typeof args[0] === "string" ? args[0] : (args[0] as Request)?.url || "";
      let pathname: string;
      try { pathname = new URL(reqUrl, window.location.origin).pathname; } catch { pathname = reqUrl; }
      const method = (args[1]?.method || "GET").toUpperCase();
      recentFailedRequests.push({ url: pathname, method, status: 0, timestamp: new Date().toISOString() });
      if (recentFailedRequests.length > MAX_FAILED_REQUESTS) recentFailedRequests.shift();
      throw err;
    }
    if (!res.ok) {
      let pathname: string;
      try { pathname = new URL(res.url).pathname; } catch { pathname = res.url; }
      const method = (args[1]?.method || "GET").toUpperCase();
      recentFailedRequests.push({ url: pathname, method, status: res.status, timestamp: new Date().toISOString() });
      if (recentFailedRequests.length > MAX_FAILED_REQUESTS) recentFailedRequests.shift();
    }
    return res;
  };

  // ── Click tracking ────────────────────────────────────────────────────────
  document.addEventListener(
    "click",
    (e) => {
      const el = e.target as HTMLElement | null;
      if (!el) return;
      // Skip password fields
      if (el.closest('input[type="password"]')) return;

      const tag = el.tagName.toLowerCase();
      const text = (el.textContent || "").trim().substring(0, 30);
      pushAction({
        type: "click",
        target: text ? `${tag}: ${text}` : tag,
        timestamp: new Date().toISOString(),
      });
    },
    true, // capture phase
  );

  // ── Navigation tracking ───────────────────────────────────────────────────
  const recordNav = (to: string) => {
    pushAction({ type: "navigation", to, timestamp: new Date().toISOString() });
  };

  window.addEventListener("popstate", () => {
    recordNav(window.location.pathname);
  });

  const originalPush = history.pushState;
  history.pushState = function (...args) {
    originalPush.apply(this, args);
    recordNav(window.location.pathname);
  };

  const originalReplace = history.replaceState;
  history.replaceState = function (...args) {
    originalReplace.apply(this, args);
    recordNav(window.location.pathname);
  };
}

// ── Getters ─────────────────────────────────────────────────────────────────

export function getRecentConsoleErrors() {
  return [...recentErrors];
}

export function getRecentFailedRequests() {
  return [...recentFailedRequests];
}

export function getRecentUserActions() {
  return [...recentUserActions];
}

// ── Device / browser helpers (unchanged) ────────────────────────────────────

export function getBrowserInfo(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox/")) return `Firefox ${ua.split("Firefox/")[1]?.split(" ")[0]}`;
  if (ua.includes("Edg/")) return `Edge ${ua.split("Edg/")[1]?.split(" ")[0]}`;
  if (ua.includes("Chrome/")) return `Chrome ${ua.split("Chrome/")[1]?.split(" ")[0]}`;
  if (ua.includes("Safari/") && !ua.includes("Chrome"))
    return `Safari ${ua.split("Version/")[1]?.split(" ")[0] || ""}`;
  return ua.substring(0, 80);
}

export function getOSInfo(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Win")) return "Windows";
  if (ua.includes("Mac")) return "macOS";
  if (ua.includes("Linux")) return "Linux";
  if (ua.includes("Android")) return "Android";
  if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
  return "Unknown";
}

export function getDeviceType(): string {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1200) return "tablet";
  return "desktop";
}

export function getViewportInfo(): string {
  return `${window.innerWidth}x${window.innerHeight}`;
}

/** Attempt to capture a screenshot of the current page via html2canvas */
export async function captureScreenshot(): Promise<Blob | null> {
  try {
    const html2canvas = (await import("html2canvas")).default;
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      allowTaint: true,
      logging: false,
      scale: 1,
      ignoreElements: (el) => {
        // Skip the dialog overlay itself
        return el.getAttribute("role") === "dialog" ||
          el.getAttribute("data-radix-portal") !== null;
      },
    });
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.8));
  } catch {
    return null;
  }
}
